import React, { useEffect, useState } from "react";
import { string } from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import { HelpButton } from "@collabkare/help";
import { resolveHelpKey } from "@/help/helpKeys";
import theme from "resources/themes";
import SLUGS from "resources/routesConstants";
import DropdownComponent from "components/Dropdown";
import { LayoutGrid, LogOut, ArrowUpRight, Bell } from "lucide-react";
import Button from "components/Button";
import { storage } from "auth/firebase";
import { useAuthStore } from "@/stores/auth-store";
import LanguagePicker from "@/components/LanguagePicker";
import useWindowSize from "@/hooks/useWindowSize";
import ckLogo from "../../assets/icons/logo-dark.svg";

import kareManager from "../../assets/appicons/kareManager.png";
import KareMD from "../../assets/appicons/kareMD.png";
import KarePortal from "../../assets/appicons/karePortal.png";
import KarePractice from "../../assets/appicons/karePractice.png";
import KareIntelligence from "../../assets/appicons/kareIntelligence.png";

// Lucide icon wrappers
const AppsIcon = (props) => <LayoutGrid size={20} {...props} />;
const LogoutIcon = (props) => <LogOut size={20} {...props} />;
const ArrowOutwardIcon = (props) => <ArrowUpRight size={14} {...props} />;
const BellIcon = (props) => <Bell size={20} {...props} />;

function HeaderComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  // MYID-H9b — collapse the long bits on narrow screens. Two breakpoints:
  //   ≤640px (phone): hide brand text + user name + Bell icon (notifications
  //                   surface in the bell dropdown anyway, sidebar has them).
  //   ≤900px (small tablet): keep brand text but hide user name.
  const [winWidth] = useWindowSize();
  const isNarrowPhone = winWidth > 0 && winWidth <= 640;
  const isCompact = winWidth > 0 && winWidth <= 900;
  const kcLogout = useAuthStore((s) => s.logout);
  const userProfile = useAuthStore((s) => s.userProfile);
  const firebaseUserData = useAuthStore((s) => s.firebaseUserData);
  const userImage = useAuthStore((s) => s.userImage);
  const [userInfo, setUserInfo] = useState();
  let [profilePic, setProiflePic] = useState();
  const accessibleApps = useAuthStore((s) => s.accessibleApps);

  // Map KC client IDs to icon images for the dropdown
  const appIconMap = {
    karemanager: kareManager,
    "kare-portal": KarePortal,
    karemd: KareMD,
    karepractice: KarePractice,
    kareintelligence: KareIntelligence,
  };

  useEffect(() => {
    if (userProfile) {
      const info = {
        name: userProfile.fullName || `${userProfile.firstName || ""} ${userProfile.lastName || ""}`.trim(),
        email: userProfile.email,
        ...(firebaseUserData || {}),
      };
      setUserInfo(info);
      if (userImage) {
        setProiflePic(userImage);
      } else if (userProfile.email) {
        getUserPic(userProfile.email);
      }
    }
  }, [userProfile, firebaseUserData, userImage]);

  const fetchImages = async (email) => {
    let result = await storage.ref().child(`documents/users_dp/${email}/`).listAll();
    let urlPromises = result.items.map((imageRef) => imageRef.getDownloadURL());
    return Promise.all(urlPromises);
  };

  const getUserPic = async (email) => {
    const urls = await fetchImages(email);
    if (urls?.length > 0) {
      profilePic = urls[0];
      setProiflePic(profilePic);
    }
  };

  // Derive apps dropdown data from KC accessibleApps (Step 8)
  const appsData = accessibleApps
    .filter((app) => app.clientId !== "my-collabkare-id")
    .map((app) => ({
      title: app.name,
      image: appIconMap[app.clientId] || kareManager,
      link: app.url,
    }));

  function getLogout() {
    return (
      <div className="flex-space" onClick={() => kcLogout()}>
        <span>Logout</span>
        <span><LogoutIcon /></span>
      </div>
    );
  }

  function getDiv() {
    return (
      <div>
        <div className="flex-centre padding10">
          <img
            src={profilePic}
            alt=""
            style={{ width: 75, height: 75, borderRadius: "50%", backgroundColor: "#ff5722", objectFit: "cover" }}
          />
        </div>
        <div>
          <span>{userInfo?.name}</span> <br />
          <span><b>{userInfo?.email}</b></span>
          <br />
          <Button
            buttonColor={"secondaryBtn"}
            onClick={() => navigate(SLUGS.profile)}
            style={{ marginTop: "5px" }}
          >
            Manage Profile
          </Button>
          <br />
        </div>
      </div>
    );
  }

  function getApps() {
    return (
      <>
        {appsData.length > 0 ? (
          <div>
            {appsData.map((item, index) => (
              <div key={index} className={"appIcons"}>
                <span style={{ padding: "10px" }}>
                  <img
                    alt={item.title}
                    src={item.image}
                    style={{ width: "95px", height: "45px", margin: "auto", overflow: "hidden", borderRadius: "8px" }}
                  />
                </span>
                <span style={{ paddingTop: "20px" }}>{item.title}</span>
                <span
                  style={{ padding: "20px", cursor: "pointer" }}
                  onClick={() => { window.location.href = item.link; }}
                >
                  <ArrowOutwardIcon />
                </span>
              </div>
            ))}
          </div>
        ) : (
          <div>No Allocated Apps</div>
        )}
      </>
    );
  }

  return (
    <div style={headerStyles.container}>
      {/* Left: Logo + App name */}
      <div style={headerStyles.leftSection}>
        <img
          src={ckLogo}
          alt="CollabKare"
          style={headerStyles.logo}
          onClick={() => navigate(SLUGS.dashboard)}
        />
        {!isNarrowPhone && (
          <>
            <div style={headerStyles.dividerVertical} />
            <span style={headerStyles.appName}>My CollabKare ID</span>
          </>
        )}
      </div>

      {/* Right: Actions.
          MYID-H1 (2026-05-25) — utility buttons (Lang, Bell, Apps) now
          grouped as a triad with consistent button shell so the area
          has clear hierarchy. Divider, then identity block. */}
      <div style={headerStyles.rightSection}>
        <div style={headerStyles.utilityGroup}>
          {/* Contextual help — learn.collabkare.com slide-over */}
          <span style={headerStyles.utilityBtnWrap}>
            <HelpButton helpKey={resolveHelpKey(location.pathname)} />
          </span>
          <LanguagePicker />
          {!isNarrowPhone && (
            <div style={headerStyles.utilityBtnWrap}>
              <DropdownComponent
                label={
                  <span style={headerStyles.utilityBtn} aria-label="Notifications">
                    <BellIcon />
                  </span>
                }
                options={[{ label: "No notifications", onClick: () => console.log("Notification") }]}
                position={{ top: 42, right: -14 }}
              />
            </div>
          )}
          <div style={headerStyles.utilityBtnWrap}>
            <DropdownComponent
              label={
                <span style={headerStyles.utilityBtn} aria-label="My apps">
                  <AppsIcon />
                </span>
              }
              options={[{ label: getApps(), onClick: "" }]}
              position={{ top: 42, right: -14 }}
            />
          </div>
        </div>
        {!isNarrowPhone && <div style={headerStyles.dividerVertical} />}
        <DropdownComponent
          label={
            <span style={headerStyles.identityBlock}>
              {!isCompact && (
                <span style={{ ...theme.typography.itemTitle, textAlign: "right" }}>
                  {userInfo?.name}
                </span>
              )}
              <span style={headerStyles.avatarWrap}>
                <Avatar profilePic={profilePic} userInfo={userInfo} />
              </span>
            </span>
          }
          options={[
            { label: getDiv(), onClick: "" },
            { label: getLogout(), onClick: "" },
          ]}
          position={{ top: 52, right: -6 }}
        />
      </div>
    </div>
  );
}

/**
 * MYID-H2 (2026-05-25) — avatar with initials fallback.
 *
 * Previously the avatar img used an orange `backgroundColor` as a
 * placeholder, then loaded the user pic from Firebase Storage via
 * `storage.ref().child(documents/users_dp/...)`. After DSC-1C.3 moved
 * the canonical photo URL to FHIR Patient.photo, the Firebase path
 * stopped returning anything for new uploads → the orange circle with
 * a broken image icon was permanent for many users.
 *
 * Fallback chain:
 *   1. `profilePic` URL (still produced by the legacy getUserPic path
 *      for users whose photos pre-date DSC-1C.3)
 *   2. Initial of firstName / userInfo.name / "?"
 * The initials path uses a stable color derived from the name so the
 * avatar is recognizable even before any pic loads.
 */
function Avatar({ profilePic, userInfo }) {
  const [imgFailed, setImgFailed] = React.useState(false);
  const name = userInfo?.name || userInfo?.first_name || "";
  const initial = (name.trim()[0] || "?").toUpperCase();
  const showImg = profilePic && !imgFailed;

  // Stable color from name (so each user has a recognizable avatar tint).
  const palette = ["#1F4E79", "#0ea5e9", "#7c3aed", "#16a34a", "#dc2626", "#d97706", "#0891b2"];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  const bg = palette[Math.abs(hash) % palette.length];

  if (showImg) {
    return (
      <img
        src={profilePic}
        alt=""
        onError={() => setImgFailed(true)}
        style={headerStyles.avatar}
      />
    );
  }
  return (
    <span
      aria-label={name || "User avatar"}
      style={{
        ...headerStyles.avatar,
        backgroundColor: bg,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#ffffff",
        fontWeight: 600,
        fontSize: 14,
      }}
    >
      {initial}
    </span>
  );
}

const headerStyles = {
  container: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    height: 56,
    padding: "0 16px 0 0",
  },
  leftSection: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  logo: {
    height: 36,
    cursor: "pointer",
    objectFit: "contain",
  },
  appName: {
    fontSize: 15,
    fontWeight: 600,
    color: "#1F4E79",
    whiteSpace: "nowrap",
  },
  dividerVertical: {
    height: 28,
    width: 1,
    backgroundColor: "#dfe0eb",
  },
  rightSection: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  // MYID-H1 — utility buttons grouped with tight 4px spacing so
  // they read as a triad, then a divider gives air before the
  // identity block on the right.
  utilityGroup: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  utilityBtnWrap: {
    cursor: "pointer",
  },
  utilityBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 36,
    height: 36,
    borderRadius: 8,
    color: "#4B5563",
    transition: "background-color 0.15s ease, color 0.15s ease",
  },
  identityBlock: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
  },
  avatarWrap: {
    display: "inline-flex",
    alignItems: "center",
  },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: "50%",
    // MYID-H2 — no more orange placeholder. The Avatar component
    // either renders the img or an initials chip; neither needs a
    // default bg here.
    backgroundColor: "transparent",
    objectFit: "cover",
    border: "2px solid #e5e7eb",
  },
};

HeaderComponent.propTypes = {
  title: string,
};

export default HeaderComponent;
